/**
 * The module defines types, components, context and utility methods related to
 * accessing the configuration data for use by the client side rendered application.
 */
import React, { createContext, useContext } from 'react';

interface LocalizationContextType {
    gt: GettextInterface;
}

/**
 * Context object storing the configuration data
 */
export const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

/**
 * Utility hook to provide the client configuration context value.  If no
 * configuration is available before the hook is dynamically invoked, then an error
 * is raised
 */
export const useLocalizationContext = (): LocalizationContextType => {
    const context = useContext(LocalizationContext);
    if (!context) {
        throw new Error('Localization context must be defined before being used');
    }

    return context;
};

interface LocalizationProviderProps {
    gt: GettextInterface;
}

/**
 * A Provider higher order component that makes the client configuration context
 * available to children component.
 * @param clientConfig
 * @param children
 * @constructor
 */
export const LocalizationProvider = ({
    gt,
    children,
}: React.PropsWithChildren<LocalizationProviderProps>): React.JSX.Element => {
    const context: LocalizationContextType = {
        gt,
    };

    return <LocalizationContext.Provider value={context}>{children}</LocalizationContext.Provider>;
};
